import { useEffect, useRef, useState } from 'react';

import { ArrowDownTrayIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';

import { useAuth } from '@/features/auth/useAuth';
import { useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import { parseUtcDate } from '@/utils/date-time';

export interface ScanFileItem {
  readonly id?: string;
  readonly name: string;
  readonly type: string;
  readonly size: string;
  readonly uploadedAt: string;
  readonly isAdditionalFile: boolean;
  readonly isProtocolFile: boolean;
  readonly isScanFile?: boolean;
}

interface ScanFilesSectionProps {
  orderId: string;
  files: readonly ScanFileItem[];
  canUploadAdditionalFiles: boolean;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseUtcDate(value));
}

function fileTypeOrder(file: ScanFileItem): number {
  if (file.isScanFile) {
    return 0;
  }

  if (file.isProtocolFile) {
    return 1;
  }

  if (file.isAdditionalFile) {
    return 2;
  }

  return 0;
}

function isDeletableFile(file: ScanFileItem): boolean {
  return Boolean(file.id && !file.isScanFile && (file.isAdditionalFile || file.isProtocolFile));
}

export function ScanFilesSection({ orderId, files, canUploadAdditionalFiles }: ScanFilesSectionProps) {
  const t = useTranslations('common.orderDetails');
  const tErrors = useTranslations('errors.api');
  const { client } = useAuth();
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(() => new Set());
  const [isDeletingFiles, setIsDeletingFiles] = useState(false);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const [errorKey, setErrorKey] = useState<ApiErrorKey | null>(null);
  const [processingFileName, setProcessingFileName] = useState<string | null>(null);
  const [failedFileName, setFailedFileName] = useState<string | null>(null);
  const [deletedFileIds, setDeletedFileIds] = useState<Set<string>>(() => new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  const visibleFiles = [...files]
    .filter((file) => !file.id || !deletedFileIds.has(file.id))
    .sort((left, right) => fileTypeOrder(left) - fileTypeOrder(right));
  const selectableFiles = visibleFiles.filter((file) => file.id);
  const selectedFiles = selectableFiles.filter((file) => selectedFileIds.has(file.id as string));
  const allFilesSelected = selectableFiles.length > 0 && selectedFiles.length === selectableFiles.length;
  const someFilesSelected = selectedFiles.length > 0 && !allFilesSelected;
  const hasSelectedScanFiles = selectedFiles.some((file) => file.isScanFile);
  const canDeleteSelectedFiles = selectedFiles.length > 0 && selectedFiles.every(isDeletableFile);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilesSelected;
    }
  }, [someFilesSelected]);

  const toggleFileSelection = (fileId: string, selected: boolean) => {
    setSelectedFileIds((previous) => {
      const next = new Set(previous);
      if (selected) {
        next.add(fileId);
      } else {
        next.delete(fileId);
      }
      return next;
    });
  };

  const handleDeleteSelectedFiles = async () => {
    const selectedCustomerFiles = selectedFiles.filter(isDeletableFile);

    if (
      !client ||
      !orderId ||
      !canUploadAdditionalFiles ||
      selectedCustomerFiles.length === 0 ||
      selectedCustomerFiles.length !== selectedFiles.length ||
      isDeletingFiles ||
      isDownloadingFiles
    ) {
      return;
    }

    if (!window.confirm(t('scanFilesSection.deleteSelectedConfirmation'))) {
      return;
    }

    setIsDeletingFiles(true);
    setErrorKey(null);
    setFailedFileName(null);
    setProcessingFileName(null);

    let operationFileName: string | null = null;
    try {
      const api = new OrdersApi(client);
      for (const file of selectedCustomerFiles) {
        operationFileName = file.name;
        setProcessingFileName(file.name);
        await api.deleteAdditionalOrderFile({ orderId, fileId: file.id as string });
        setDeletedFileIds((previous) => {
          const next = new Set(previous);
          next.add(file.id as string);
          return next;
        });
        setSelectedFileIds((previous) => {
          const next = new Set(previous);
          next.delete(file.id as string);
          return next;
        });
        operationFileName = null;
        setProcessingFileName(null);
      }
    } catch (error) {
      setFailedFileName(operationFileName);
      setErrorKey(errorKeyOf(error));
    } finally {
      setProcessingFileName(null);
      setIsDeletingFiles(false);
    }
  };

  const handleDownloadSelectedFiles = async () => {
    if (!client || !orderId || selectedFiles.length === 0 || isDeletingFiles || isDownloadingFiles) {
      return;
    }

    setIsDownloadingFiles(true);
    setErrorKey(null);
    setFailedFileName(null);
    setProcessingFileName(null);

    let operationFileName: string | null = null;
    try {
      const api = new OrdersApi(client);
      for (const file of selectedFiles) {
        operationFileName = file.name;
        setProcessingFileName(file.name);
        const blob = await api.downloadOrderFile({ orderId, fileId: file.id as string });
        const downloadUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = file.name;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
        operationFileName = null;
        setProcessingFileName(null);
      }
    } catch (error) {
      setFailedFileName(operationFileName);
      setErrorKey(errorKeyOf(error));
    } finally {
      setProcessingFileName(null);
      setIsDownloadingFiles(false);
    }
  };

  const handleFileRowClick = (fileId: string | undefined) => {
    if (!fileId || isDeletingFiles || isDownloadingFiles) {
      return;
    }

    setSelectedFileIds((previous) => {
      const next = new Set(previous);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleSelectAllChange = (selected: boolean) => {
    if (isDeletingFiles || isDownloadingFiles) {
      return;
    }

    setSelectedFileIds(selected ? new Set(selectableFiles.map((file) => file.id as string)) : new Set());
  };

  if (visibleFiles.length === 0) {
    return (
      <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
        <h2 className="m-0">{t('scanFilesSection.title')}</h2>
        <p className="mt-2 text-sm text-[var(--zs-muted)]">{t('scanFilesSection.noFiles')}</p>
      </section>
    );
  }

  return (
    <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
      <div className="flex flex-wrap items-center justify-between gap-3 max-sm:mb-[0.4rem] max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
        <h2 className="m-0">{t('scanFilesSection.title')}</h2>
        <div className="flex flex-wrap items-center gap-2 max-sm:grid max-sm:w-full max-sm:grid-cols-[minmax(0,1fr)_repeat(2,2.75rem)] max-sm:items-center max-sm:gap-2">
          <span className="min-w-0 text-sm text-[var(--zs-muted)] max-sm:wrap-anywhere">
            {t('scanFilesSection.selectedCount', { count: selectedFiles.length })}
          </span>
          <button
            type="button"
            className="zs-button zs-button--secondary md-icon-button max-sm:justify-self-stretch"
            disabled={
              isDeletingFiles || isDownloadingFiles || !canUploadAdditionalFiles || !canDeleteSelectedFiles
            }
            onClick={() => void handleDeleteSelectedFiles()}
            aria-label={
              isDeletingFiles
                ? t('scanFilesSection.deleteSelectedBusy')
                : t('scanFilesSection.deleteSelectedAction')
            }
            title={t('scanFilesSection.deleteSelectedAction')}
          >
            <TrashIcon className="md-icon-button__icon" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="zs-button zs-button--secondary md-icon-button max-sm:justify-self-stretch"
            disabled={isDeletingFiles || isDownloadingFiles || selectedFiles.length === 0}
            onClick={() => void handleDownloadSelectedFiles()}
            aria-label={
              isDownloadingFiles
                ? t('scanFilesSection.downloadSelectedBusy')
                : t('scanFilesSection.downloadSelectedAction')
            }
            title={t('scanFilesSection.downloadSelectedAction')}
          >
            <ArrowDownTrayIcon className="md-icon-button__icon" aria-hidden="true" />
          </button>
        </div>
      </div>
      {hasSelectedScanFiles ? (
        <p className="mt-2 text-sm text-[var(--zs-muted)]" role="status" aria-live="polite">
          {t('scanFilesSection.scanFilesCannotBeDeleted')}
        </p>
      ) : null}
      {processingFileName ? (
        <p
          className="mt-2 flex items-center gap-2 text-sm text-[var(--zs-muted)]"
          role="status"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
          {isDeletingFiles
            ? t('scanFilesSection.deletingFile', { file: processingFileName })
            : t('scanFilesSection.downloadingFile', { file: processingFileName })}
        </p>
      ) : null}
      {errorKey ? (
        <p className="mt-2 text-sm text-[var(--md-highlight)]" role="alert">
          {failedFileName ? `${t('scanFilesSection.fileFailed', { file: failedFileName })} ` : null}
          {tErrors(errorKey)}
        </p>
      ) : null}
      <table className="md-order-details__files-table w-full border-collapse">
        <thead>
          <tr>
            <th className="w-[3rem] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allFilesSelected}
                disabled={selectableFiles.length === 0 || isDeletingFiles || isDownloadingFiles}
                onChange={(event) => handleSelectAllChange(event.target.checked)}
                aria-label={t('scanFilesSection.selectAllFiles')}
              />
            </th>
            {(['file', 'type', 'size', 'uploadedAt'] as const).map((column) => (
              <th
                key={column}
                className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]"
              >
                {t(`scanFilesSection.columns.${column}` as never)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleFiles.map((file) => (
            <tr
              key={file.id ?? file.name}
              className={file.id ? 'cursor-pointer hover:bg-[var(--md-bg)]' : undefined}
              tabIndex={file.id ? 0 : undefined}
              onClick={() => handleFileRowClick(file.id)}
              aria-selected={file.id ? selectedFileIds.has(file.id) : undefined}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleFileRowClick(file.id);
                }
              }}
            >
              <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                <input
                  type="checkbox"
                  checked={Boolean(file.id && selectedFileIds.has(file.id))}
                  disabled={!file.id || isDeletingFiles || isDownloadingFiles}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    if (file.id) {
                      toggleFileSelection(file.id, event.target.checked);
                    }
                  }}
                  aria-label={`${t('scanFilesSection.selectFile')}: ${file.name}`}
                />
              </td>
              <td
                className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]"
                data-label={t('scanFilesSection.columns.file')}
              >
                <strong>{file.name}</strong>
                {processingFileName === file.name ? (
                  <ArrowPathIcon
                    className="ml-2 inline-block h-4 w-4 animate-spin align-[-0.15em]"
                    aria-label={t('scanFilesSection.processingFile')}
                  />
                ) : null}
                {/* {file.isScanFile ? (
                  <span className="ml-2 rounded-full bg-[var(--md-primary-light)] px-2 py-0.5 text-xs font-bold text-[var(--md-primary)]">
                    {t('scanFilesSection.scanBadge')}
                  </span>
                ) : file.isProtocolFile ? (
                  <span className="ml-2 rounded-full bg-[var(--md-primary-light)] px-2 py-0.5 text-xs font-bold text-[var(--md-primary)]">
                    {t('additionalFilesSection.protocolBadge')}
                  </span>
                ) : file.isAdditionalFile ? (
                  <span className="ml-2 rounded-full bg-[var(--md-primary-light)] px-2 py-0.5 text-xs font-bold text-[var(--md-primary)]">
                    {t('additionalFilesSection.additionalBadge')}
                  </span>
                ) : null} */}
              </td>
              <td
                className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]"
                data-label={t('scanFilesSection.columns.type')}
              >
                {file.type}
              </td>
              <td
                className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]"
                data-label={t('scanFilesSection.columns.size')}
              >
                {file.size}
              </td>
              <td
                className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]"
                data-label={t('scanFilesSection.columns.uploadedAt')}
              >
                {formatDateTime(file.uploadedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
